import AmplifyButton from '@amplify/amplify-button';
import clsx from 'clsx';
import { MouseEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AssignmentsViewOptions } from 'src/interfaces/assignment.types';
import { Theme } from 'src/interfaces/theme.types';
import { useCurrentGradeSelector } from 'src/store/features';
import { getTheme } from 'src/utils/theme.utils';

import './status-change-dialog.scss';

type StatusChangeDialogProps = {
  isVisible?: boolean;
  cancelStatusChange?: (e: MouseEvent) => void;
  toggleStatus?: (e: MouseEvent) => void;
  isCardRemovalAnimationInProgress?: boolean;
  section?: AssignmentsViewOptions.TODO | AssignmentsViewOptions.PAST_WORK;
};

export const StatusChangeDialog = ({
  isVisible,
  cancelStatusChange,
  toggleStatus,
  isCardRemovalAnimationInProgress,
  section,
}: StatusChangeDialogProps) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const isPastWorkSectionActive = section === AssignmentsViewOptions.PAST_WORK;
  const isToDoSectionActive = section === AssignmentsViewOptions.TODO;

  const currentGrade = useCurrentGradeSelector();
  const theme = getTheme(currentGrade, false);

  useEffect(() => {
    const containerElement = containerRef.current;

    if (!isVisible || !containerElement) return;

    containerElement.focus();
  }, [isVisible]);

  return (
    <div
      className={clsx('status-change-dialog', {
        'status-change-dialog--past-work': isPastWorkSectionActive,
      })}
      tabIndex={isVisible ? 0 : -1}
      ref={containerRef}
    >
      <div
        className={clsx('status-change-dialog__background', {
          'status-change-dialog__background--removed-to-do':
            isCardRemovalAnimationInProgress && isToDoSectionActive,
        })}
      />
      <div
        className={clsx('status-change-dialog__content', {
          'status-change-dialog__content--reduced-to-do':
            isVisible && isToDoSectionActive,
          'status-change-dialog__content--removed-to-do':
            isCardRemovalAnimationInProgress && isToDoSectionActive,
          'status-change-dialog__content--past-work': isPastWorkSectionActive,
          'status-change-dialog__content--reduced-past-work':
            isVisible && isPastWorkSectionActive,
        })}
      >
        {isToDoSectionActive && (
          <p className="status-change-dialog__title">{t('Are you done?')}</p>
        )}
        <div className="theme-k5 layout-context-delta status-change-dialog__buttons-group">
          {isToDoSectionActive && (
            <>
              <div className="status-change-dialog__button-container">
                <AmplifyButton
                  variant="primary"
                  className={clsx(
                    `type-scale-minus-1`,
                    'status-change-dialog__cancel-btn-to-do',
                    `status-change-dialog__cancel-btn-to-do--${theme}`,
                    {
                      'btn-raised': theme === Theme.SECOND_THIRD,
                    },
                  )}
                  icon="icon-k5-edit"
                  tabIndex={isVisible ? 0 : -1}
                  onClick={cancelStatusChange}
                >
                  {t('Still working')}
                </AmplifyButton>
              </div>
              <div className="status-change-dialog__button-container">
                <AmplifyButton
                  variant="secondary"
                  className={clsx(
                    `type-scale-minus-1`,
                    'status-change-dialog__complete-btn-to-do',
                    `status-change-dialog__complete-btn-to-do--${theme}`,
                    {
                      'btn-raised': theme === Theme.SECOND_THIRD,
                    },
                  )}
                  icon="icon-k5-checkmark"
                  tabIndex={isVisible ? 0 : -1}
                  onClick={toggleStatus}
                >
                  {t("I'm done")}
                </AmplifyButton>
              </div>
            </>
          )}
          {isPastWorkSectionActive && (
            <>
              <div className="bg-dark status-change-dialog__button-container">
                <AmplifyButton
                  variant="secondary"
                  className={clsx(
                    'type-scale-minus-1',
                    'status-change-dialog__cancel-btn-past-work',
                    {
                      'btn-raised': theme === Theme.SECOND_THIRD,
                    },
                  )}
                  icon="icon-k5-close"
                  tabIndex={isVisible ? 0 : -1}
                  onClick={cancelStatusChange}
                >
                  {t('Cancel')}
                </AmplifyButton>
              </div>
              <div className="bg-dark status-change-dialog__button-container">
                <AmplifyButton
                  variant="primary"
                  className={clsx(
                    'type-scale-minus-1',
                    'status-change-dialog__complete-btn-past-work',
                    `status-change-dialog__complete-btn-past-work--${theme}`,
                    {
                      'btn-raised': theme === Theme.SECOND_THIRD,
                    },
                  )}
                  icon="icon-k5-undo"
                  tabIndex={isVisible ? 0 : -1}
                  onClick={toggleStatus}
                >
                  {t('Mark as To Do')}
                </AmplifyButton>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
